import { createSelector, Selector } from '@ngxs/store';
import { UserProfileState, UserProfileStateModel } from './user-profile.state';
import { SportsSelectors } from '../../sports/state/sports.selectors';
import { UserSportDisciplineLevel, UserSportLevel } from '../domains/user-sport-level';
import {
  DeleteUserEligibilityResponseDto,
  NotificationsEntity,
  SportDefinitionResume,
  UserProfilePopulatedEntity,
} from '@smatchy/sdk';
import { GeoLocality, GeoPosition } from '../../locations';
import { DeleteUserProfileErrorCode } from './user-profile.state';
import { formatLocationSuggestionDetail, inferAdminLevelFromHierarchy } from '../../../utils/resolve-admin-level.util';
import { showsEnergyEstimatesOf } from '../domains/the-energy-estimates-switch';

export class UserProfileSelector {
  @Selector([UserProfileState])
  static loading(state: UserProfileStateModel): boolean {
    return state.loading;
  }

  @Selector([UserProfileState])
  static loading_delete(state: UserProfileStateModel): boolean {
    return state.deletingProfile;
  }

  @Selector([UserProfileState])
  static deleteEligibilityLoading(state: UserProfileStateModel): boolean {
    return state.deleteEligibilityLoading;
  }

  @Selector([UserProfileState])
  static deleteEligibilityLoaded(state: UserProfileStateModel): boolean {
    return state.deleteEligibilityLoaded;
  }

  @Selector([UserProfileState])
  static deleteEligibility(state: UserProfileStateModel): DeleteUserEligibilityResponseDto | undefined {
    return state.deleteEligibility;
  }

  @Selector([UserProfileState])
  static deleteProfileError(state: UserProfileStateModel): DeleteUserProfileErrorCode | undefined {
    return state.deleteProfileError;
  }

  @Selector([UserProfileState])
  /** No avatar before there is a profile, which is what the `?.` already answered. */
  static avatar(state: UserProfileStateModel): string | undefined {
    return state.profile?.avatarUrl;
  }

  @Selector([UserProfileState])
  /** Absent until a profile is loaded, which is the state this selector also describes. */
  static notifications(state: UserProfileStateModel): NotificationsEntity | undefined {
    return state.profile?.notifications;
  }

  @Selector([UserProfileState])
  /** Same: nobody's language before there is a profile. */
  static language(state: UserProfileStateModel): string | undefined {
    return state.profile?.language;
  }

  @Selector([UserProfileState])
  /**
   * Absent until the profile is loaded. Said out loud here because callers
   * already knew it: several of them read this with `?.`, which is only
   * meaningful if it can answer nothing.
   */
  static profile(state: UserProfileStateModel): UserProfilePopulatedEntity | undefined {
    return state.profile;
  }

  /** Counted in the nearby Smatchers total: on unless explicitly turned off. */
  @Selector([UserProfileState])
  static appearInNearbyCount(state: UserProfileStateModel): boolean {
    return state.profile?.appearInNearbyCount !== false;
  }

  /**
   * Shown what a session costs in energy: on unless explicitly turned off.
   *
   * The generated client does not carry this field yet, so the reading lives
   * in the domain next to the weight it is used with.
   */
  @Selector([UserProfileState])
  static showsEnergyEstimates(state: UserProfileStateModel): boolean {
    return showsEnergyEstimatesOf(state.profile);
  }

  @Selector([UserProfileState])
  static userId(state: UserProfileStateModel): string | undefined {
    return state.profile?.userId;
  }

  @Selector([UserProfileState])
  static displayName(state: UserProfileStateModel): string {
    const emailPrefix = state.profile?.user?.email ? state.profile.user.email.split('@')[0] : '';
    const firstName = state.profile?.firstName || emailPrefix;
    const lastName = state.profile?.lastName || '';

    if (state.profile?.isMyLastNameAnonymised && lastName) {
      return `${firstName} ${lastName.substring(0, 1)}.`.trim();
    } else {
      return `${firstName} ${lastName}`.trim();
    }
  }

  @Selector([UserProfileState])
  static location(state: UserProfileStateModel): GeoPosition | undefined {
    if (state.profile?.location?.lat && state.profile.location?.lon) {
      return {
        lat: state.profile.location.lat,
        lng: state.profile.location.lon,
      };
    }
    return;
  }

  @Selector([UserProfileState])
  /** The empty string before a profile is loaded: the screens draw it as text. */
  static city(state: UserProfileStateModel): string {
    return state.profile?.city ?? '';
  }

  @Selector([UserProfileState])
  static cityWithCountry(state: UserProfileStateModel): string {
    if (!state.profile) {
      return '';
    }
    return state.profile.city + ', ' + state.profile.country;
  }

  @Selector([UserProfileState])
  static gender(state: UserProfileStateModel): string {
    switch (state.profile?.sex) {
      case 'male':
        return 'MAN';
      case 'female':
        return 'WOMAN';
      default:
        return 'NO_GENDER';
    }
  }

  @Selector([UserProfileState])
  /** The body already returns nothing for an absent profile; the signature said otherwise. */
  static locality(state: UserProfileStateModel): Partial<GeoLocality> | undefined {
    if (!state.profile) {
      return undefined;
    }
    const hierarchy = state.profile.location?.hierarchy;
    const adminLevel = inferAdminLevelFromHierarchy(hierarchy) || undefined;
    const city = hierarchy?.city?.name || (adminLevel === 'city' || !adminLevel ? state.profile.city : undefined);
    const region = hierarchy?.region?.name || (adminLevel === 'region' ? state.profile.city : undefined);
    const country = hierarchy?.country?.name || state.profile.country;
    const suggestionDetail = formatLocationSuggestionDetail({
      city: city || hierarchy?.city?.name,
      department: hierarchy?.department?.name,
      region: region || hierarchy?.region?.name,
      country,
    });
    return {
      city: city || state.profile.city,
      country,
      countryCode: hierarchy?.country?.code,
      formattedAddress: state.profile.location?.name || suggestionDetail || state.profile.city,
      placeId: state.profile.location?.placeId,
      region,
      hierarchy,
      adminLevel,
      suggestionDetail,
    };
  }

  @Selector([UserProfileState, SportsSelectors.getActiveSports])
  static userSportsLevel(state: UserProfileStateModel, sports: SportDefinitionResume[]): UserSportLevel[] {
    // One row per sport, not per stored level. A single-level sport has one
    // level and reads exactly as before; a per-discipline sport (ski) stores one
    // level per discipline, which are gathered here into a single row that
    // carries the breakdown.
    const levels = state.profile?.sportLevels ?? [];
    const bySport = new Map<string, typeof levels>();
    for (const level of levels) {
      const forSport = bySport.get(level.sportId) ?? [];
      forSport.push(level);
      bySport.set(level.sportId, forSport);
    }

    return [...bySport.entries()]
      .map(([sportId, entries]) => {
        const sport = sports.find((s) => s._id === sportId);
        if (!sport) {
          return undefined;
        }
        const maxLevel = sport.maxLevel || 1;
        const percentOf = (level: number): number => (level / maxLevel) * 100;

        const disciplineEntries = entries.filter((entry) => entry.disciplineId && entry.level !== undefined);

        if (disciplineEntries.length > 0) {
          const disciplines: UserSportDisciplineLevel[] = disciplineEntries.map((entry) => ({
            disciplineId: entry.disciplineId as string,
            level: entry.level as number,
            percent: percentOf(entry.level as number),
            date: entry.date,
            answerSentence: entry.answerSentence ?? '',
          }));
          // The sport's headline figure is its strongest discipline, so the
          // one-row summary in lists shows what the member is best at.
          const overall = disciplines.reduce((best, d) => (d.level > best.level ? d : best), disciplines[0]);
          return new UserSportLevel(
            sport._id,
            sport.icon,
            overall.level,
            overall.percent,
            sport.name,
            overall.date,
            '',
            disciplines,
          );
        }

        const userSport = entries.find((entry) => entry.level !== undefined);
        if (!userSport || userSport.level === undefined) {
          return undefined;
        }
        return new UserSportLevel(
          sport._id,
          sport.icon,
          userSport.level,
          percentOf(userSport.level),
          sport.name,
          userSport.date,
          userSport.answerSentence ?? '',
        );
      })
      .filter((s): s is UserSportLevel => !!s);
  }

  static userSportLevel(sportKeyId: string) {
    return createSelector([UserProfileSelector.userSportsLevel], (state: UserSportLevel[]) =>
      state.find((s) => s.id === sportKeyId),
    );
  }

  static userSportLevelById(sportId: string) {
    return createSelector([UserProfileSelector.userSportsLevel], (state: UserSportLevel[]) =>
      state.find((s) => s.id === sportId),
    );
  }

  @Selector([UserProfileState])
  static isProfileComplete(state: UserProfileStateModel): boolean {
    const profile = state.profile;

    return !!(
      profile?.firstName &&
      profile.firstName.trim() !== '' &&
      profile?.lastName &&
      profile.lastName.trim() !== '' &&
      profile?.location?.lat &&
      profile?.location?.lon
    );
  }

  @Selector([UserProfileState])
  static missingProfileFields(state: UserProfileStateModel): string[] {
    const profile = state.profile;
    const missing: string[] = [];
    if (!profile?.firstName || profile.firstName.trim() === '') {
      missing.push('firstName');
    }
    if (!profile?.lastName || profile.lastName.trim() === '') {
      missing.push('lastName');
    }
    if (!profile?.location?.lat || !profile?.location?.lon) {
      missing.push('location');
    }

    return missing;
  }

  @Selector([UserProfileState])
  static hasPushToken(state: UserProfileStateModel): boolean {
    const existNotificationsAsked = localStorage.getItem('notificationsAsked');
    return !!state.profile?.pushToken && existNotificationsAsked === 'true';
  }
}
